<script setup lang='ts'>
import { notify } from '@kyvg/vue3-notification';
import { inject, ref } from 'vue';
import { Head, Link, router } from '@inertiajs/vue3'
import { InvoiceResource, OrdersResource } from '@/types/Resources';
import { useFormatTime } from '@/Composables/useFormatTime';
import { useFormatCurrency } from '@/Composables/useFormatCurrency';
import Tag from '@/Components/Utils/Tag.vue';
import Icon from '@/Components/Utils/Icon.vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import LoadingIcon from '@/Components/Utils/LoadingIcon.vue';
import { aikuLocaleStructure } from '@/Composables/useLocaleStructure';

const props = defineProps<{
    order: OrdersResource
    isLoadingEditOrder?: number | null
    index: number
    invoice: InvoiceResource
    invoiceCross?: InvoiceResource
    selectedEditOrder: OrdersResource | null
    isModalEditOrder: boolean
}>()

const emits = defineEmits<{
    (e: 'selectedEditOrder', value: OrdersResource): void
    (e: 'isModalEditOrder', value: boolean): void
}>()

const locale = inject('locale', aikuLocaleStructure)

// Section: Move order
const isLoadingMoveOrder = ref<number[]>([])
const onMoveOrder = (idInvoiceTarget: number | undefined, idInvoiceFrom: number, idOrder: number) => {
    console.log('idinvoice', idInvoiceTarget)
    if (!idInvoiceTarget) {
        notify({
            title: 'Target invoice tidak ada',
            text: '',
            type: 'error',
        })

        return
    }
    router.patch(
        route('master.invoices.move_order', [idInvoiceTarget]),
        {
            invoice_id_from: idInvoiceFrom,
            orders: [idOrder]
        },
        {
            preserveScroll: true,
            onStart: () => {
                // isLoadingOrder.value = true
                isLoadingMoveOrder.value.push(idOrder)
            },
            onFinish: () => {
                const index = isLoadingMoveOrder.value.indexOf(idOrder)
                if (index > -1) {
                    isLoadingMoveOrder.value.splice(index, 1)
                }
                // isLoadingOrder.value = false
            },
            onSuccess: () => {
                notify({
                    title: 'Berhasil!',
                    text: 'Memindahkan order ke invoice lain',
                    type: 'success',
                })
            },
            onError: (err) => {
                notify({
                    title: 'Oops!',
                    text: 'Gagal memindahkan order.',
                    type: 'error',
                })
                console.error(err)
            },
        }
    )
}


// Section: Unselect order
const isLoadingUnselectOrder = ref<number[]>([])
const onUnselectOrder = (idInvoice: number, order_id: number) => {
    console.log('idinvoice', idInvoice)
    if (!idInvoice) {
        notify({
            title: 'Pilih invoice terlebih dahulu',
            text: '',
            type: 'error',
        })

        return
    }

    router.patch(
        route('master.invoices.remove_order', [idInvoice]),
        {
            // invoice_id: props.invoice.id,
            order: order_id
        },
        {
            onStart: () => {
                isLoadingUnselectOrder.value.push(order_id)
            },
            onFinish: () => {
                const index = isLoadingUnselectOrder.value.indexOf(order_id)
                if (index > -1) {
                    isLoadingUnselectOrder.value.splice(index, 1)
                }
            },
            onError: () => notify({
                title: 'Oops!',
                text: 'Gagal menghapus order',
                type: 'error',
            }),
        }
    )
}
</script>

<template>
    <li
        class="first:border-t-0 border-t border-t-gray-300 relative py-3 flex flex-col lg:flex-row gap-x-3 pr-2"
        :class="[
            order.is_have_discount ? 'border-l-4 border-l-sky-300 bg-sky-50' : '',
        ]"
    >
        <div v-if="isLoadingEditOrder === order?.id" class="absolute inset-0 opacity-50">
            <div class="skeleton h-full"></div>
        </div>
        <div class="px-3 flex-grow grid grid-cols-4 lg:grid-cols-7 gap-x-4 ">
            <div class="col-span-3 flex gap-x-2">
                <div class="text-gray-500">{{ index+1 }}.</div>
                <div class="space-y-0.5">
                    <h3 class="font-bold">
                        <Link :href="route('master.orders-m.show', order.slug)" class="primaryLink">
                            {{ order.reference }}
                        </Link>
                        <Tag v-if="order.discount_in_percentage > 0" xlabel="order.discount_in_percentage" :theme="3" icon="fal fa-badge-percent" noHoverColor>
                            <template #label>
                                <FontAwesomeIcon icon='fas fa-tag' class='' fixed-width aria-hidden='true' />
                                {{ order.discount_in_percentage }}%
                            </template>
                        </Tag>
                    </h3>

                    <!-- Product name -->
                    <p class="text-gray-500">
                        <Icon :data="order.product?.type_icon" />
                        {{ order.product?.name }}
                    </p>

                    <!-- Field: Keterangan Order -->
                    <p v-tooltip="'Keterangan Order'" class="lgxhidden text-gray-500 tabular-nums">
                        <FontAwesomeIcon icon='fal fa-comment-alt' class='' fixed-width aria-hidden='true' />
                        <span class="ml-1"> {{ order.keterangan_cetak }} </span>
                    </p>

                    <p class="lg:hidden text-gray-500 tabular-nums">
                        <FontAwesomeIcon icon='fal fa-dollar-sign' class='' fixed-width aria-hidden='true' />
                        <span class="ml-1" :class="order.quantity_show !== order.quantity_by_type ? 'text-sky-600' : ''">{{ locale.number(order.quantity_show) }} pcs</span>, <span :class="order.product_price_show !== order.product?.price ? 'text-sky-600' : ''">{{ useFormatCurrency(order.product_price_show) }}/pcs</span>
                    </p>
                    <p class="text-gray-500">
                        <FontAwesomeIcon icon='fal fa-copyright' class='' fixed-width aria-hidden='true' />
                        {{ order.brand?.name ?? '-' }}
                    </p>
                    <p class="text-gray-500 italic">
                        <FontAwesomeIcon icon='fal fa-calendar-alt' class='' fixed-width aria-hidden='true' />
                        {{ useFormatTime(order.created_at) }}
                    </p>
                </div>
            </div>
    
            <!-- Column: Price -->
            <p class="hidden lg:block flex-none text-right font-medium tabular-nums">
                <template v-if="order.product_price_show !== order.product?.price">
                    <span class="line-through text-gray-400" >
                        {{ useFormatCurrency(order.product?.price ?? 0) }}/pcs
                    </span>
                    <span class="text-sky-600">
                        {{ useFormatCurrency(order.product_price_show) }}/pcs
                    </span>
                </template>
                <template v-else>
                    {{ useFormatCurrency(order.product_price_show) }}/pcs
                </template>
            </p>
            <!-- Column: Quantity -->
            <p class="hidden lg:block flex-none text-right font-medium tabular-nums">
                <template v-if="order.quantity_show !== order.quantity_by_type">
                    <span class="line-through text-gray-400" >
                        {{ locale.number(order.quantity_by_type) }} pcs
                    </span>
                    <br>
                    <span class="text-sky-600">
                        {{ locale.number(order.quantity_show) }} pcs
                    </span>
                </template>
                <template v-else>
                    {{ locale.number(order.quantity_show) }} pcs
                </template>
            </p>
            <!-- Column: Total -->
            <p class="lg:col-span-2 text-right font-medium tabular-nums">
                <span class="" :class="order.discount_in_percentage > 0 ? 'line-through text-gray-400' : ''">{{ useFormatCurrency(order.total_amount_show) }}</span><br>
                <span v-if="order.discount_in_percentage > 0" class="text-sky-600 font-semibold">
                    {{ useFormatCurrency(order.final_total ?? 0) }}
                </span>
            </p>
        </div>
        
        <!-- Button: move & unselect -->
        <div class="w-fit flex self-end lg:self-start flex-row lg:flex-col flex-wrap flex-shrink items-end gap-x-1 gap-y-1.5">
            <Transition name="spin-to-right">
                <div v-if="!(!!invoice.billing_date || !!invoiceCross?.billing_date)" v-tooltip="invoice.type === 'cash' ? 'Pindah ke invoice transfer' : 'Pindah ke invoice cash'" @click="async () => onMoveOrder(invoiceCross?.id, invoice.id, order.id)" class="h-fit rounded bg-gray-100 hover:bg-gray-300 border border-gray-300 text-lg cursor-pointer px-2 py-1">
                    <FontAwesomeIcon v-if="!isLoadingMoveOrder.includes(order.id)" :icon="invoice.type === 'cash' ? 'fal fa-arrow-left' : 'fal fa-arrow-right'" class='' fixed-width aria-hidden='true' />
                    <LoadingIcon v-else class="" />
                </div>
            </Transition>
            <div v-if="!invoice.billing_date" v-tooltip="'Hapus dari list'" @click="() => onUnselectOrder(invoice.id, order.id)" class="h-fit rounded hover:bg-red-200 border border-transparent hover:border-red-500 text-red-400 hover:text-red-600 text-lg cursor-pointer px-2 py-1">
                <FontAwesomeIcon v-if="!isLoadingUnselectOrder.includes(order.id)" icon='fal fa-times' class='' fixed-width aria-hidden='true' />
                <LoadingIcon v-else class="" />
            </div>
            <div v-if="!invoice.billing_date" v-tooltip="'Edit Order ini'" @click="() => (emits('selectedEditOrder', {...order}) , emits('isModalEditOrder', true))" class="h-fit rounded hover:bg-gray-200 border border-transparent hover:border-gray-500 text-gray-400 hover:text-gray-600 text-lg cursor-pointer px-2 py-1">
                <FontAwesomeIcon icon='fal fa-pencil' class='' fixed-width aria-hidden='true' />
            </div>
        </div>
    </li>
        
        
</template>